import { useContext, useEffect, useState } from "react";
import RegistrationLayout from "../../Components/RegistrationLayout/RegistrationLayout";
import { RegistrationContext } from "../../Context/RegistrationContext";
import LoadingTransparent from "../../Components/Loading/LoadingTransparent";
import correctIcon from "../../Assets/correct.svg";
import { showToast } from "../../Components/Toaster/Toaster";
import ReactSignatureCanvas from "react-signature-canvas";
import ImageAdd from '../../Assets/Add image.svg';
import { getMediaUrl } from "../../lib/utils/utils";
import { registerSeller } from "../../lib/utils/apiCalls";
import useAuthentication from "../../Hooks/useAuthentication";
import { useNavigate } from "react-router-dom";

function SectionComponent() {
    const navigate = useNavigate();
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    const [signatureCanvas, setSignatureCanvas] = useState(null);

    const {
        fetchedEmail,
        // Business info
        gstDetails,
        gstCardInfo,

        // Store Setup
        storeDetails,
        storeCategory,
        targetAudience,
        warehouseAddress,
        bankDetails,
        signatureType,
        setSignatureType,
        signatureFile,
        setSignatureFile,
        chequeFile,

        setLoading,

        agreementErrors,
        setAgreementErrors,

        markerPositionWarehouse,
    } = useContext(RegistrationContext);

    const { setAuthenticated } = useAuthentication();

    const [isButtonAvailable, setButtonAvailable] = useState(false);
    
    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
        setIsScrolledToBottom(bottom);
    };

    async function processSign(file) {
        if (!file) return;

        // Size validation (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            setAgreementErrors({
                ...agreementErrors,
                signUrl: 'File size should be less than 10MB',
            });
            return;
        }

        // ✅ File accepted
        setSignatureFile(file);
        setAgreementErrors({
            ...agreementErrors,
            signUrl: '',
        });
    }

    const handleClearSignature = () => {
        if (signatureCanvas) {
            signatureCanvas.clear();
        }
    };

    const handleDropSign = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files[0];
        await processSign(file);
    };

    const handleDragOverSign = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeaveSign = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    async function handleSignatureSelection(e) {
        const file = e.target.files[0];
        await processSign(file);
        e.target.value = null; // reset input for re-upload
    }

    async function handleDownload() {
        const link = document.createElement("a");
        link.href = "/Agreement_Legal.pdf";
        link.download = "Seller Associate Agreement_Legal with Annexure 1.pdf";
        link.click();
    };

    const handleCheck = () => {
        if(isScrolledToBottom) {
            if(signatureType === 'signatureDraw' && signatureCanvas.isEmpty()) {
                showToast('Please draw your signature', 'warning');
                return;
            }

            if (
                signatureType === '' ||
                (signatureType === 'signatureUpload' && (!signatureFile || !(signatureFile instanceof File) || signatureFile.size === 0))
            ) {
                showToast('Please upload your signature', 'warning');
                return;
            }
            setIsChecked(!isChecked);
        }
        else {
            showToast('Scroll agreement to the end to continue.', 'warning');
        }
    };

    async function handleSubmit() {
        if(!isButtonAvailable) {
            showToast('Please select the check box.', 'warning');
            return;
        }

        setLoading(true);

        let signatureDataUrl;

        if(signatureType === 'signatureDraw') {
            const signatureData = signatureCanvas.toDataURL("image/png");
            try {
                const blob = await (await fetch(signatureData)).blob();
                const signatureWithFilename = new Blob([blob], {
                    type: "image/jpeg",
                });
                signatureWithFilename.name = "signature.jpg";
                signatureDataUrl = await getMediaUrl(signatureWithFilename);
            }
            catch (error) {
                console.error("Error converting signature to blob:", error);
            }
        }
        else if(signatureType === 'signatureUpload') {
            signatureDataUrl = await getMediaUrl(signatureFile);
        }

        let chequeDataUrl = await getMediaUrl(chequeFile);

        let gstAddress = [ 
            gstCardInfo.addressTotal?.building,
            gstCardInfo.addressTotal?.floor,
            gstCardInfo.addressTotal?.street,
            gstCardInfo.addressTotal?.district,
            gstCardInfo.addressTotal?.state && gstCardInfo.addressTotal?.zip 
            ? `${gstCardInfo.addressTotal.state} - ${gstCardInfo.addressTotal.zip}` 
            : gstCardInfo.addressTotal?.state || gstCardInfo.addressTotal?.zip
        ]
        .filter(Boolean)
        .join(', ')

        const { description, ...storeDetailsSpread } = storeDetails;

        try {
            const callData = {
                ...gstDetails,
                businessName: gstCardInfo.legalname,
                panNo: gstCardInfo.panNumber,
                address: gstAddress,
                ...storeDetailsSpread,
                targetAudience,
                storeCategory: storeCategory.map(item => item.label),
                warehouseAddr: {
                    ...warehouseAddress,
                    longitude: markerPositionWarehouse.lat.toFixed(6),
                    latitude: markerPositionWarehouse.lng.toFixed(6),
                },
                accountHolderName: bankDetails.accountHolderName,
                accountNumber: bankDetails.accountNumber,
                ifscCode: bankDetails.ifscCode,
                signUrl: signatureDataUrl,
                cancelledChequeUrl: chequeDataUrl,
            }

            if(storeDetails?.description) {
                callData.storeDescription = description;
            }

            const response = await registerSeller(callData);

            if(response.status === 200) {
                setLoading(false);
                setAuthenticated(response.data.data.accessToken, response.data.data.refreshToken);
                const roles = response.data.data.role;
                roles.sort();
                if (roles.includes("Admin")) {
                    navigate("/");
                    window.location.reload();
                } else if (roles.includes("Inventory Manager")) {
                    navigate("/inventory-management");
                    window.location.reload();
                } else if (roles.includes("Lens Manager")) {
                    navigate("/lens-and-reviews");
                    window.location.reload();
                } else if (roles.includes("Store Manager")) {
                    navigate("/storefront");
                    window.location.reload();
                } else {
                    navigate("/");
                    window.location.reload();
                }
            }
        }
        catch(err) {
            showToast(err, 'error');
            setLoading(false);
        }
    }

    useEffect(() => {
        if(isChecked) {
            setButtonAvailable(true);
        }
        else {
            setButtonAvailable(false);
        }
    }, [isChecked]);

    return (
        <div className='flex flex-col gap-6'>
            {/* Header Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-start justify-between">
                    <div className="w-full">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Seller Agreement</h1>

                        <p className="text-gray-600 mb-4">This is a legal agreement between you and Xenie.</p>

                        <button
                            onClick={handleDownload}
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors group"
                        >
                            <svg
                                className="w-5 h-5 group-hover:translate-y-0.5 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            Download Agreement
                        </button>
                    </div>

                    {fetchedEmail && (
                        <div className="bg-blue-50 px-4 py-2 rounded-lg">
                            <p className="text-sm text-gray-600">Registered Email</p>
                            <p className="font-semibold text-gray-900">{fetchedEmail}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Agreement Content (Updated - full pages with {{}} for blanks) */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6 relative">
                <div
                    id="agreement-content"
                    onScroll={handleScroll}
                    className="h-[70vh] overflow-y-auto p-8 space-y-6 scroll-smooth"
                    style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#CBD5E0 #F7FAFC"
                    }}
                >
                    <div className="prose max-w-none flex flex-col gap-3">
                        {/* Title Page */}
                        <h2 className="text-xl font-bold text-gray-900 mb-4">SELLER ASSOCIATION AGREEMENT</h2>

                        <p className="text-gray-700 mb-2">
                            <strong>THIS VENDOR SERVICE AGREEMENT (THIS "AGREEMENT") DATED AS OF 10.07.2025 IS
                            MADE BY AND BETWEEN:</strong>
                        </p>

                        <p className="text-gray-700 mb-2">
                            <strong>Branch X India Ltd.</strong>, a Public Limited Company incorporated under the provisions of the
                            Companies Act, 2013 and having its Registered office at <strong>{"7th floor, BSEL Tech Park, 708/709/710, B Wing, Inorbit Bypass, Sector 30A, Vashi, Navi Mumbai, Maharashtra-400703"}</strong> (Hereinafter
                            called "Branch X" which expression, unless inconsistent with or repugnant to the context or meaning,
                            thereof, shall mean and include its successors and assigns) of the One part.
                        </p>

                        <p className="text-gray-700 mb-2">
                            <strong>{gstDetails?.ownerName}</strong>, having its office at <strong>{`${storeDetails?.storeAddress}, ${storeDetails?.storeCity}, ${storeDetails?.storeState} - ${storeDetails?.storePincode}`}</strong> (Hereinafter called "SELLER" which expression,
                            unless inconsistent with or repugnant to the context or meaning, thereof, shall mean and include its
                            successors and assigns) of the Other Part.
                        </p>

                        <p className="text-gray-700 mb-2">
                            <strong>WHEREAS</strong> Branch X have created a platform named “XENIE” dedicated to empowering
                            fashion brands to showcase and sell their products online for which the Seller herein has
                            approached Branch X seeking rights and access to showcase their products through Branch
                            X.
                        </p>

                        <p className="text-gray-700 mb-2">
                            <strong>WHEREAS</strong> Branch X has herein agreed to provide its services to the Seller in order to
                            conclude the proposed business activity and facilitate the sale of products the Seller intends
                            to sell through XENIE as per the terms and conditions agreed and recorded herein below.
                        </p>

                        <p className="text-gray-700 mb-2">
                            <strong>NOW THEREFORE</strong>, in consideration of the mutual promises, Branch X and the Seller hereby
                            intending to be legally bound, hereby agree to abide by following provisions and agree to make
                            the following additions that are outlined below which will govern the obtaining and providing of
                            the Services:
                        </p>

                        <p className="text-gray-700 mb-2">
                            <strong>IN CONSIDERATION OF THE PROMISSES AND COVENANTS CONTAINED HEREIN, AS
                            WELL AS OTHER GOOD AND VALUABLE CONSIDERATION, THE PARTIES DO
                            HEREBY AGREES AND FOLLOWS:</strong> 
                        </p>

                        {/* Section 1 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-blue-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Definitions</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>"Xenie" refers to the online platform operated by Branch X India Limited.</li>
                                <li>"Brand" or "Seller" refers to the entity registering to sell its products on Xenie.</li>
                                <li>"Platform" refers to the website and associated services provided by Xenie.</li>
                            </ul>
                        </div>

                        {/* Section 2 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-green-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Terms and Conditions</h3>

                            <p className="text-gray-700">
                                By entering into this Agreement, the Seller has acknowledged that the terms and
                                conditions of this proposed business have been read, understood, and agreed in
                                accordance with specific terms of this Agreement between both parties.
                            </p>
                        </div>

                        {/* Section 3 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-purple-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Eligibility</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>
                                    The Seller is required to be legally registered in India and its business should be
                                    operational in status at the time of execution of this Agreement.
                                </li>
                                <li>
                                    The Seller is bound to comply with all applicable laws and regulations applicable
                                    within the Jurisdiction of India also including Indian Contact Act, 1872.
                                </li>
                                <li>
                                    The Seller herein acknowledges that Xenie reserves its right to select which brands
                                    are eligible to be sold on the platform based on criteria such as product quality,
                                    brand reputation, and alignment with Xenie's values and objectives thereby
                                    maintaining the standards of business and products traded through its platform.
                                </li>
                            </ul>
                        </div>

                        {/* Section 4 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-orange-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Registration</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>The Seller herein is liable to submit and fill the Online Registration Form along with all relevant details and proof of business.</li>
                                <li>The Seller is also liable to provide all documentary evidence and proofs with
                                    respect to its business activities and products.</li>
                                <li>
                                    The seller may be required to provide additional documentation and verification to
                                    complete the Registration process upon which subject to successful registration
                                    process the Seller will be granted exclusive access to the Platform (XENIE) and its
                                    services.
                                </li>
                            </ul>
                        </div>

                        {/* Section 5 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-teal-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">5. Obligations and Duties of Seller</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>The Seller will be responsible for handling any queries from customers and clients
                                    with respect to their products which are provided online through XENIE.</li>
                                <li>The Seller will be liable to maintain accurate data, product listings, Pricing,
                                    Inventory of Stock, management of Customer Orders and queries.</li>
                                <li>The Seller will also be liable to fulfil orders promptly and ensure compliance with
                                    Indian Consumer Protection Act, 2019.</li>
                                <li>
                                    The seller will also be responsible for dealing with Customer Service issues and
                                    disputes, the Seller will also be liable to resolve the said issues and disputes within
                                    the stipulated turnaround time i.e. 24 hours and return processing of products to
                                    be done within 48 hours of return receipt.
                                </li>
                                <li>
                                    Sellers are strictly prohibited from attempting to redirect Branch X/Xenie customers
                                    to make purchases outside the platform via offers or referrals. Violation of this
                                    clause will lead to immediate penalties, including but not limited to account
                                    suspension or termination without notice.
                                </li>
                                <li>Seller must ensure that all items are securely and appropriately packaged,
                                    following Xenie's packaging guidelines to prevent damage during transit. Noncompliance may result in penalties or account suspension.</li>
                                <li>Sellers must regularly update inventory status and ensure real-time stock sync to
                                    avoid order cancellations. Repeated failure may attract penalties.</li>
                                <li>
                                    The Seller will be evaluated on the basis of their performance using KPIs based on
                                    Order Cancellation Rate, Return Rate, Feedback and ratings etc.
                                </li>
                                <li>
                                    Sellers must comply with Xenie's return policy. They must accept returns for
                                    reasons defined in the return policy. Reverse logistics will be arranged by Xenie's
                                    logistics partner, with costs borne by the seller if the fault lies with the seller (e.g.,
                                    wrong/damaged product).
                                </li>
                                <li>
                                    The Seller also herein agrees that the account may be suspended or blacklisted in
                                    the following cases:
                                    <ul className="list-disc ml-4 mt-2 flex flex-col gap-2">
                                        <li>Poaching of customers.</li>
                                        <li>Repeated violations.</li>
                                        <li>Breach of Platform rules and applicable laws.</li>
                                    </ul>
                                </li>
                                <li>
                                    Seller will responsible for GST compliance. All invoices must be generated by the
                                    seller. Xenie may deduct applicable TCS/TDS before remitting payouts. Sellers are
                                    responsible for filing taxes as per government norms.
                                </li>
                            </ul>
                        </div>

                        {/* Section 6 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-cyan-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">6. Obligations of Branch X</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Create awareness by way of Digital Advertising of Platform and generally promote
                                    the business.</li>
                                <li>Promote/market/advertise offers through its website, in mutually agreed ways.</li>
                                <li>
                                    Shall not make any promise or representation or shall not give any warranties or
                                    guarantees to any clients which is not supported by Seller and perform such other
                                    duties as may be mutually decided by parties from time to time.
                                </li>
                            </ul>
                        </div>

                        {/* Section 7 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-red-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">7. Force Majeure</h3>

                            <p className="text-gray-700">
                                Neither Party shall be held liable for any failure or delay in performing its obligations
                                under this Agreement if such failure or delay is due to causes beyond its reasonable
                                control, including but not limited to natural disasters, pandemics, government
                                regulations, strikes, or cyberattacks. The affected Party shall promptly notify the other
                                Party of the occurrence of such event and use reasonable efforts to resume
                                performance as soon as practicable.
                            </p>
                        </div>

                        {/* Section 8 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-amber-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">8. Financials, Commercials and Fees</h3>

                            <p className="text-gray-700">
                                Commission rates and sale closure fees are specifically outlined in Annexure A which
                                forms a part of this Seller Agreement. Additional fees may apply for onboarding,
                                subscription, premium listings, and advertising.
                            </p>
                        </div>

                        {/* Section 9 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-sky-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">9. Representation</h3>

                            <h4 className="text-gray-700 mb-2">Parties hereby represent that:</h4>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>It has the power to enter into this joint promotion activity (offer) with each other.</li>
                                <li>
                                    All acts, conditions and things required to be done, fulfilled and performed to
                                    ensure that the obligations assumed by it under this letter of offer are legal,
                                    valid, binding and enforceable have been done, fulfilled and performed.
                                </li>
                                <li>The obligations assumed by it under this letter of offer are legal, valid, binding
                                    and enforceable against it.</li>
                            </ul>
                        </div>

                        {/* Section 10 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-indigo-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">10. Compliance with Laws</h3>

                            <p className="text-gray-700">
                                Parties agree that it shall at all times and at its own expense (a) strictly comply with all
                                applicable Indian laws, rules, regulations and governmental orders, now or hereafter in effect,
                                relating to its performance of this joint offer, (b) pay all fees and other charges required by
                                such laws, rules and regulations and orders and (c) maintain in full force and effect all licenses,
                                permits, authorization, registration and qualifications from all Indian Governmental
                                departments and agencies to the extent necessary to perform its obligations hereunder.
                            </p>
                        </div>

                        {/* Section 11 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-fuchsia-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">11. Non-Disclosure</h3>

                            <p className="text-gray-700">
                                Either party agrees that all information and details of the Agreement and the Offer which either
                                party may know or learn during the process of transaction, is confidential information of such
                                other party and shall maintain the confidential information in strict confidentiality by storing it
                                in the same manner as it stores its own confidential information. Either Party shall not disclose
                                the confidential information to any person without the prior written consent of the other party
                                provided that such party may disclose such information (i) as per orders or directions of court
                                of law or statutory authorities, or (ii) to its employees/Service Providers on strictly need-toknow basis who will be bound by all the terms of this Agreement. In addition to exercising such
                                degree of care to protect the confidential information, either party also agrees not to in any
                                way to disclose, copy, reproduce, modify, use, or otherwise transfer the Confidential
                                Information to any other person, group of persons or entity (whether incorporated or not)
                                without obtaining prior express written consent from the other party. Either Party or his
                                authorized persons shall not reverse engineer, disassemble or decompile any prototypes,
                                software or other tangible objects, which embody the Confidential Information. Either Party
                                retains the sole and exclusive ownership as well as all the intellectual property rights in the
                                Confidential Information and no license or any other interest is granted unless expressly
                                agreed to by the other party in writing prior to such disclosure. All derivatives of such
                                confidential information shall qualify as 'confidential information' and this obligation on the
                                other party to maintain confidentiality will continue beyond termination of this Agreement as
                                per the terms and conditions of this Agreement.
                            </p>
                        </div>

                        {/* Section 12 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-emerald-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">12. Severability</h3>

                            <p className="text-gray-700">
                                If any of the provisions of this letter of offer become invalid, illegal or unenforceable in any
                                respect under any law, the validity legality and enforceability of the remaining provisions shall
                                not in any way be affected or impaired.
                            </p>
                        </div>

                        {/* Section 13 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-yellow-600">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">13. Indemnity</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>
                                    Seller hereby expressly agrees to indemnify and keep indemnified Branch X ( XENIE)
                                    India at all times and make good any loss, damage, suffered or incurred due to any
                                    willful act by the staff/employee/personnel/representative of the Seller, in the
                                    performance of its duties and obligations. Seller further agree to indemnify and keep
                                    indemnified Branch X from and against any loss, damage or liability arising as a result
                                    of any act of omission or commission or in respect of non-observance of any statutory
                                    requirement with respect to the offer.
                                </li>
                                <li>
                                    Further, Seller shall indemnify, defend and hold harmless Branch X India from any and
                                    all claims, costs, liabilities, judgments, expenses and damages (including reasonable
                                    attorney's fees) arising out of reputation risk to us or failure to provide the offer.
                                </li>
                                <li>
                                    The Seller herein agrees to indemnify and keep indemnified BranchX from claims,
                                    compensations, demands, litigation, complaints, notices, prosecution from any govt.
                                    or Semi- Govt. Authorities like Cyber Crime Depts., GST Dept. Income Tax etc. for
                                    cause of actions arising due to any act or business concluded through the Seller, the
                                    said indemnification also covers any actions or consequences arising through any third
                                    party personnel be it individual or company which may conclude its business with
                                    BranchX through the Seller. The Seller herein agrees to safeguard the interest of
                                    BranchX under all circumstances wherein he/ she is directly or indirectly related.
                                </li>
                            </ul>
                        </div>

                        {/* Section 14 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-gray-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">14. Term and Termination</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>
                                    Unless otherwise terminated, either party reserves the right to terminate this offer
                                    by giving a 30 days prior notice in writing to the other party in the event the offer is
                                    not launched by concerned party for reasons beyond the reasonable control of the
                                    concerned party.
                                </li>
                                <li>
                                    Notwithstanding the above, the provisions of this offer regarding confidentially,
                                    provisions of indemnification and all obligations of Parties arising prior to the
                                    expiration or termination of this offer shall survive the expiration or termination of
                                    this offer for the period of 3 months.
                                </li>
                            </ul>
                        </div>

                        {/* Section 15 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-rose-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">15. Arbitration</h3>

                            <p className="text-gray-700">
                                The parties agree that all disputes in connection with any aspect of this agreement shall be
                                submitted to final, binding arbitration by one (1) arbitrator reasonably acceptable to both
                                parties with expertise relevant to the dispute. any such arbitration shall be held in the district
                                of Mumbai and conducted under the then-current commercial arbitration rules of the arbitration
                                and conciliation act, 1996. if the parties cannot agree upon an arbitrator, an arbitrator shall be
                                selected in accordance with such rules. each party shall share equally in the cost of arbitration
                                and shall bear its own expenses including professional fees.
                            </p>
                        </div>

                        {/* Section 16 */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-stone-500">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">16. General Terms</h3>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>
                                    <strong>Entire Agreement:</strong> This Agreement, the Fee Schedules, and all applicable account
                                    disclosure notices, and the agreements governing the Accounts (each, an “Account
                                    Agreement”) are the complete and exclusive statement of the understanding and
                                    agreement between Seller and Branch X with respect to the subject matter hereof, and
                                    supersede any prior agreements between Seller and Branch X with respect to such
                                    subject matter. The Services are also subject to the terms of the User Manual, as in
                                    effect from time to time. In the event of any inconsistency between the terms of this
                                    Agreement and any Account Agreement or the User Manual, the terms of this
                                    Agreement will control with respect to the Services.
                                </li>
                                <li>
                                    <strong>Amendments:</strong> If performance of the Services may result in a violation of any present or
                                    future statute, regulation, or government policy this Agreement will be deemed
                                    amended to the extent necessary to facilitate compliance with such statute, regulation
                                    or policy, Except as expressly provided in this paragraph, this Agreement may not be
                                    modified or amended except by a written agreement duly executed by Seller and
                                    Branch X.
                                </li>
                                <li>
                                    <strong>No Waiver:</strong> No waiver or partial or single exercise by either party of any right or remedy
                                    under this Agreement will be of any force and effect unless made expressly in writing.
                                </li>
                                <li>
                                    <strong>No Beneficiaries:</strong> This Agreement is for the benefit only of the undersigned parties
                                    hereto and is not intended to and shall not be construed as granting any rights to or
                                    otherwise benefiting any other person.
                                </li>
                                <li>
                                    <strong>No Third-Party Use:</strong> Unless prior written consent, the Seller may not use the Services
                                    to process transactions for third parties or permit others to initiate Service transactions
                                    on your behalf.
                                </li>
                                <li>
                                    <strong>Headings:</strong> Headings to sections of this Agreement or any Exhibit are included for each
                                    of reference and shall not be deemed to create rights, remedies, claims, or defenses
                                    arising under the Agreement.
                                </li>
                                <li>
                                    <strong>Severability:</strong> In the event that any court or tribunal of competent jurisdiction determines
                                    that any provision of the Agreement is illegal, invalid, or unenforceable, the remainder
                                    of this Agreement shall not be affected thereby.
                                </li>
                                <li>
                                    <strong>Validity:</strong> If any provision of this Agreement is found to be void or invalid, the remainder
                                    of this Agreement will remain in full force and effect.
                                </li>
                                <li>
                                    <strong>Waivers:</strong> Any waiver by us must be in writing to be effective. Our waiver of any right
                                    will not be deemed a waiver of other rights or of the same right at another time.
                                </li>
                                <li>
                                    <strong>Governing Law:</strong> This Agreement will be governed by the laws of the India.
                                </li>
                            </ul>
                        </div>

                        {/* Execution */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                            <p className="text-gray-800 font-medium">
                                <strong>IN WITNESS WHEREOF</strong>, the parties hereto have executed this Agreement, or caused this
                                Agreement to be executed by their duly authorized representatives, as of the date first above
                                written.
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white border border-gray-200 rounded-lg p-4 h-fit">
                                <p className="text-gray-900">For <strong>BRANCH X India Limited</strong></p>

                                <div className="w-1/2">
                                    <img src="https://imageserver.xenie.in/1761630311102.jpg" alt="" />
                                </div>

                                <div className="mt-4 text-gray-700 flex flex-col gap-1">
                                    <p>Mudit Bhatnagar</p>
                                    <p>Chief Commercial Officer</p>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <p className="font-semibold text-gray-900">
                                    For <span className="underline">{gstDetails?.ownerName}</span> (Seller)
                                </p>

                                <div className="mt-4">
                                    <input
                                        type="radio"
                                        id="signatureDraw"
                                        name="signature"
                                        checked={signatureType === 'signatureDraw'}
                                        onChange={() => setSignatureType('signatureDraw')}
                                        className="hidden" 
                                        value='signatureDraw' 
                                    />

                                    <input
                                        type="radio"
                                        id="signatureUpload"
                                        name="signature"
                                        checked={signatureType === 'signatureUpload'}
                                        onChange={() => setSignatureType('signatureUpload')}
                                        className="hidden" 
                                        value='signatureUpload' 
                                    />

                                    <div className="flex flex-col gap-1">
                                        <label
                                            htmlFor="signatureDraw"
                                            className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2"
                                        >
                                            <div
                                                className={`size-4 border-2 ${signatureType === 'signatureDraw' ? 'border-primaryColor' : 'border-text2'} flex items-center justify-center rounded-full`}
                                            >
                                                {
                                                    signatureType === 'signatureDraw' && 
                                                    <div className="size-2 bg-primaryColor rounded-full"></div>
                                                }
                                            </div>
                                            Draw your signature on the screen
                                        </label>

                                        {
                                            signatureType === 'signatureDraw' && 
                                            <div className="drawer">
                                                <ReactSignatureCanvas
                                                    ref={(ref) =>
                                                        setSignatureCanvas(ref)
                                                    }
                                                    canvasProps={{
                                                        width: 325,
                                                        height: 180,
                                                        className: "sigCanvas",
                                                    }}
                                                />
                                                <span
                                                    className="absolute right-3 bottom-3 text-sm text-red-500"
                                                    onClick={handleClearSignature}
                                                >Clear
                                                </span>
                                            </div>
                                        }

                                        <label 
                                            htmlFor="signatureUpload"
                                            className="cursor-pointer font-normal text-[14px] leading-6 text-text1 flex items-center gap-2"
                                        >
                                            <div
                                                className={`size-4 border-2 ${signatureType === 'signatureUpload' ? 'border-primaryColor' : 'border-text2'} flex items-center justify-center rounded-full`}
                                            >
                                                {
                                                    signatureType === 'signatureUpload' && 
                                                    <div className="size-2 bg-primaryColor rounded-full"></div>
                                                }
                                            </div>
                                            Upload signature
                                        </label>

                                        {
                                            signatureType === 'signatureUpload' && 
                                            <>
                                                <label 
                                                    onDrop={handleDropSign} 
                                                    onDragOver={handleDragOverSign} 
                                                    onDragLeave={handleDragLeaveSign} 
                                                    htmlFor='signature' className="cursor-pointer border-dashed bg-[#E5E5E5] border-2 border-[#D1D5DB] w-full h-[140px] rounded-lg flex flex-col items-center justify-center gap-2"
                                                >
                                                    <div className="w-12 h-12 flex items-center justify-center">
                                                        <img src={ImageAdd} alt="" />
                                                    </div>

                                                    <p className='font-medium text-sm text-[#4B5563] w-[90%] text-center'>
                                                        <span className='text-[#4F46E5]'>Click to Upload</span> or Drag or drop
                                                    </p>

                                                    <p className='font-medium text-[14px] leading-5 text-[#6B7280]'>PNG, JPG, PDF up to 10MB</p>
                                                </label>

                                                <p className='font-medium text-base text-text1'>{signatureFile.name}</p>

                                                <input 
                                                    onChange={handleSignatureSelection} 
                                                    className='hidden'
                                                    type="file" 
                                                    accept="image/png, image/jpeg" 
                                                    name="signature" 
                                                    id="signature" 
                                                />
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Annexure A: Xenie Commercial Policy */}
                        <h3 className="text-2xl font-bold text-gray-900 mt-6 mb-4">Annexure – Xenie Commercial Policy</h3>

                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-blue-600">
                            <h4 className="font-semibold text-gray-900 mb-2">1. Platform Usage</h4>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Use the Xenie Platform solely for listing and selling products per Xenie policies.</li>
                                <li>Ensure accurate, compliant product listings.</li>
                                <li>Maintain up-to-date brand and business info in the Seller Portal.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-green-600">
                            <h4 className="font-semibold text-gray-900 mb-2">2. Registration & Listing</h4>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Provide accurate business entity, GST, bank account, and product catalog details.</li>
                                <li>Meet Xenie’s image, content, and pricing guidelines.</li>
                                <li>Misleading/duplicate listings may be suspended or delisted without prior notice.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-yellow-600">
                            <h4 className="font-semibold text-gray-900 mb-2">3. Sale Closure Fee</h4>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Below ₹1000: ₹12 + GST per transaction.</li>
                                <li>₹1000 and above: ₹18 + GST per transaction.</li>
                                <li>Deducted from Seller’s settlement for delivered orders; not applicable for cancellations/returns before delivery completion.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-teal-600">
                            <h4 className="font-semibold text-gray-900 mb-2">4. Delivery</h4>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Deliveries via Xenie’s authorized logistics partners.</li>
                                <li>Secure packaging, ready as per dispatch schedule; adhere to packaging standards.</li>
                                <li>Delivery charges debited to Seller account as per actuals.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-indigo-600">
                            <h4 className="font-semibold text-gray-900 mb-2">5. Payment Terms</h4>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Orders from 1st–15th → Payment on/before 20th.</li>
                                <li>Orders from 16th–Month End → Payment on/before 5th of next month.</li>
                                <li>
                                    Payments to registered bank account after deducting commission, fees, and valid return adjustments.
                                    Xenie will notify of delays due to banking/technical issues.
                                </li>
                            </ul>
                        </div>

                        {/* Commission Tables */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-orange-600">
                            <h4 className="font-semibold text-gray-900 mb-2">6. Commission Structure</h4>

                            <h5 className="font-semibold text-gray-900 mt-4 mb-2">A. Fashion & Beauty Category</h5>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-yellow-100">
                                            <th className="border p-3 text-left">Product Category (Examples)</th>
                                            <th className="border p-3 text-center">Commission (≤ ₹1000)</th>
                                            <th className="border p-3 text-center">Commission (&gt; ₹1000)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ["Accessories – Fashion Jewellery, Handbags, Wallets, Watches & Perfumes", "14%", "16%"],
                                            ["Apparel – Men’s Wear (Incl. Innerwear, Sportswear, Sleepwear)", "14%", "16%"],
                                            ["Apparel – Women’s Wear (Incl. Innerwear, Kurtas, Chudis, Tops, Pants)", "14%", "16%"],
                                            ["Apparel – Women’s Sarees", "16%", "18%"],
                                            ["Eyewear – Sunglasses & Frames", "16%", "18%"],
                                            ["Footwear – Shoes, Flip Flops & Fashion Sandals", "14%", "16%"],
                                            ["Kidswear & Accessories", "14%", "16%"],
                                            ["Skin and Body Care / Hair & Body Care", "14%", "16%"],
                                            ["Travel Accessories (Backpacks, Trolleys, Laptop Bags)", "14%", "16%"],
                                            ["Fragrances (Perfumes, Body Mists, Deodorants)", "14%", "18%"]
                                        ].map((row, i) => (
                                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                                <td className="border p-3">{row[0]}</td>
                                                <td className="border p-3 text-center font-semibold text-blue-600">{row[1]}</td>
                                                <td className="border p-3 text-center font-semibold text-blue-600">{row[2]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">B. Electronics & Accessories Category</h5>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-yellow-100">
                                            <th className="border p-3 text-left">Product Category (Examples)</th>
                                            <th className="border p-3 text-center">Xenie Commission Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ["Speakers & Soundbars (Bluetooth, Mini, Soundbars)", "8%"],
                                            ["Wireless Earbuds / Headphones / Earphones (TWS, Neckbands, Over-Ear)", "14%"],
                                            ["Chargers, Cables & Power Accessories (Adapters, Power Banks, Cables)", "12%"],
                                            ["Smart Wearables / Fitness Bands (Smartwatches, Trackers)", "8%"],
                                            ["Computer & Gaming Accessories (Keyboards, Mice, Headsets)", "9%"],
                                            ["Lamps / Home & Office Gadgets (LED, Study Lamps)", "9%"],
                                            ["Personal Care Gadgets (Trimmers, Grooming Kits)", "8%"],
                                            ["TVs / Monitors / Laptops / Cameras", "6%"],
                                            ["Smart Home, Car & Photography Accessories (Bulbs, Chargers, Ring Lights, Tripods)", "9%"]
                                        ].map((row, i) => (
                                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                                <td className="border p-3">{row[0]}</td>
                                                <td className="border p-3 text-center font-semibold text-blue-600">{row[1]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">C. Home Décor Category</h5>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-yellow-100">
                                            <th className="border p-3 text-left">Product Category (Examples)</th>
                                            <th className="border p-3 text-center">Xenie Commission Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ["Decor & Table Accents (Vases, Showpieces, Figurines)", "12%"],
                                            ["Wall Art & Frames (Canvas Prints, Mirrors)", "12%"],
                                            ["Home Lighting (Non-Functional) (Decorative Lights, Fairy Lights)", "10%"],
                                            ["Organizers & Storage Décor (Trays, Shelves, Hooks)", "10%"],
                                            ["Textile & Cushion Décor (Cushion Covers, Throws, Curtains)", "12%"]
                                        ].map((row, i) => (
                                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                                <td className="border p-3">{row[0]}</td>
                                                <td className="border p-3 text-center font-semibold text-blue-600">{row[1]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <p className="text-gray-700 mt-2">Note: All commission rates attract GST as applicable.</p>
                        </div>

                        {/* Return & Replacement Policy */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-red-600">
                            <h4 className="font-semibold text-gray-900 mb-2">7. Return & Replacement Policy</h4>

                            <h5 className="font-semibold text-gray-900 mt-4 mb-2">A. General Terms</h5>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Returns & Refunds only—no exchanges/replacements.</li>
                                <li>All returns initiated by buyers via Xenie; sellers will not communicate directly with buyers regarding returns.</li>
                                <li>Xenie manages pickup, QC, and refund processing. Refunds are treated as reversal of sale.</li>
                            </ul>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">B. Return Eligibility</h5>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-red-100">
                                            <th className="border p-3 text-left">Category</th>
                                            <th className="border p-3 text-left">Return Eligibility</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="bg-gray-50">
                                            <td className="border p-3">Fashion & Accessories</td>
                                            <td className="border p-3">
                                                Accepted within 7 days for wrong, damaged, or defective items. Unused, unwashed, with all tags.
                                            </td>
                                        </tr>
                                        <tr className="bg-white">
                                            <td className="border p-3">Electronics & Accessories</td>
                                            <td className="border p-3">Accepted within 7 days for defective or wrong item.</td>
                                        </tr>
                                        <tr className="bg-gray-50">
                                            <td className="border p-3">Home Décor</td>
                                            <td className="border p-3">Accepted within 7 days for damaged or incorrect products.</td>
                                        </tr>
                                        <tr className="bg-white">
                                            <td className="border p-3">Beauty & Personal Hygiene</td>
                                            <td className="border p-3">
                                                Non-returnable, except for wrong/expired product (reported within 48 hrs via Xenie Support).
                                            </td>
                                        </tr>
                                        <tr className="bg-gray-50">
                                            <td className="border p-3">Innerwear (Men & Women)</td>
                                            <td className="border p-3">Non-returnable for hygiene reasons.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">C. Return & Refund Process</h5>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Buyer raises request with reason and evidence; Seller alerted via Dashboard (Order ID, reason, proofs).</li>
                                <li>Seller must act within 24 hours: Approve or Reject with valid justification (auto-approval if no action).</li>
                                <li>QC at pickup; if passes, refund is initiated immediately; credited to buyer within 5–7 working days.</li>
                                <li>Refunds auto-adjusted in Seller’s next settlement; Seller may dispute QC outcome within 48 hours.</li>
                                <li>
                                    If Seller wrongly rejects, Xenie may approve refund, debit from Seller’s payout, and/or flag account for
                                    review.
                                </li>
                            </ul>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">D. Simplified Return Workflow</h5>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-red-100">
                                            <th className="border p-3 text-left">Stage</th>
                                            <th className="border p-3 text-left">Actor</th>
                                            <th className="border p-3 text-left">Action / Responsibility</th>
                                            <th className="border p-3 text-left">Timeline</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ["Return Initiated", "Buyer", "Reason + evidence via Xenie", "Within return window"],
                                            ["Return Notification", "Xenie → Seller", "Order, reason, proof shared", "Immediate"],
                                            ["Seller Decision", "Seller", "Approve / Reject (auto-approve if no action)", "24 hours"],
                                            ["QC at Pickup", "Xenie / 3PL", "Validate reason with item condition", "Same day"],
                                            ["Refund Initiation", "Xenie", "Trigger refund if QC passes", "Within 5–7 working days"],
                                            ["Seller Dispute", "Seller", "Post-refund dispute (if any)", "Within 48 hours"],
                                            ["Xenie Review", "Xenie", "Final decision after review", "Within 3 working days"],
                                            ["Buyer Dispute (if seller rejection)", "Buyer → Xenie", "Appeal within 48 hrs of rejection", "Within 48 hours"]
                                        ].map((r, i) => (
                                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                                <td className="border p-3">{r[0]}</td>
                                                <td className="border p-3">{r[1]}</td>
                                                <td className="border p-3">{r[2]}</td>
                                                <td className="border p-3">{r[3]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">E. Cost Responsibility</h5>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-red-100">
                                            <th className="border p-3 text-left">Reason for Return</th>
                                            <th className="border p-3 text-left">Who Bears the Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="bg-gray-50">
                                            <td className="border p-3">Wrong, damaged, or defective item</td>
                                            <td className="border p-3">Seller</td>
                                        </tr>
                                        <tr className="bg-white">
                                            <td className="border p-3">Buyer reason (change of mind, size, color) where applicable</td>
                                            <td className="border p-3">Xenie or Buyer (as applicable)</td>
                                        </tr>
                                        <tr className="bg-gray-50">
                                            <td className="border p-3">Beauty / Innerwear / Non-returnable items</td>
                                            <td className="border p-3">Seller</td>
                                        </tr>
                                        <tr className="bg-white">
                                            <td className="border p-3">Rejected return after QC (used/tampered)</td>
                                            <td className="border p-3">Xenie / Buyer (as applicable)</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">F. Seller Guidelines</h5>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>Maintain accurate listings, images, and product details.</li>
                                <li>Ensure high-quality, tamper-proof packaging.</li>
                                <li>Avoid dispatching defective or mislabelled products.</li>
                                <li>Multiple valid returns may trigger quality audits by Xenie.</li>
                            </ul>

                            <h5 className="font-semibold text-gray-900 mt-6 mb-2">G. Legal Clause</h5>

                            <ul className="list-disc pl-6 text-gray-700 flex flex-col gap-2">
                                <li>All refunds, QC results, and dispute resolutions executed by Xenie are binding on the Seller.</li>
                                <li>This policy supersedes prior return/refund communications.</li>
                                <li>Xenie reserves the right to update this policy with due notice.</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                {!isScrolledToBottom && (
                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none flex items-end justify-center pb-2">
                        <div className="animate-bounce text-gray-400 text-xs flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                            Scroll to read complete agreement
                        </div>
                    </div>
                )}
            </div>

            {/* Agreement Checkbox */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6 select-none">
                <div className="flex items-start gap-4 cursor-pointer group" onClick={handleCheck}>
                    <div
                        className={`flex-shrink-0 w-6 h-6 rounded transition-all duration-200 flex items-center justify-center ${
                            isChecked ? "bg-primaryColor border-none" : "border border-gray-300 group-hover:border-primaryColor"
                        }`}
                    >
                        {isChecked && <img src={correctIcon} alt="Checked" className="w-4 h-4" />}
                    </div>

                    <p className="text-gray-700 leading-relaxed flex-1">
                        By checking this box I confirm that I have read and agree to abide by this Seller Agreement and the Xenie Seller
                        Terms and Conditions.
                    </p>
                </div>
            </div>

            <button onClick={handleSubmit} className={`w-full ${isButtonAvailable ? 'bg-primaryColor hover:bg-primaryAccent' : 'bg-buttonDisabledBg'} text-white px-3 py-4 rounded-[9px] font-semibold text-[18px] leading-6`}>Let’s get in</button>
        </div>
    )
}

function Agreement() {
    const {
        loading,
    } = useContext(RegistrationContext);

    return (
        <>
            {loading && <LoadingTransparent />}

            <RegistrationLayout>
                <SectionComponent />
            </RegistrationLayout>
        </>
    )
}

export default Agreement;
